<template>
	<div
		@mousemove="handleInteraction"
		@mouseleave="handleMouseLeave"
		@touchstart="handleInteraction"
		class="position-relative bg-black"
	>
		<!-- :poster="poster" -->
		<video
			ref="videoPlayer"
			class="w-100 h-100 br-4"
			:src="src"
			:poster="poster"
			@click="togglePlayPause"
			controlsList="nodownload nofullscreen noremoteplayback"
			disablePictureInPicture
		></video>
		<div
			ref="videoControls"
			:class="['custom-controls video', { show: showControls }]"
			@mouseenter="isMouseOverControls = true"
			@mouseleave="isMouseOverControls = false"
		>
			<input
				type="range"
				min="0"
				:max="videoDuration"
				step="0.1"
				v-model="currentTime"
				@input="onSeek"
				class="seekbar"
				:style="{ '--progress': (currentTime / videoDuration) * 100 + '%' }"
			/>
			<div class="d-flex justify-content-between align-items-center mt-2">
				<div class="d-flex align-items-center gap-3">
					<span @click="togglePlayPause" class="pointer timer-txt">
						<span v-if="!isPlaying"
							><img src="@/assets/asset-bank/play.svg"
						/></span>
						<span v-else><img src="@/assets/asset-bank/pause.svg" /></span>
					</span>
					<span class="mt-1"
						>{{ formattedCurrentTime }} / {{ formattedDuration }}</span
					>
					<span @click="rewind" class="pointer">
						<img src="@/assets/asset-bank/backward.svg" />
					</span>
					<span @click="forward" class="pointer">
						<img src="@/assets/asset-bank/forward.svg" />
					</span>
				</div>
				<div class="d-flex align-items-center gap-3 mt-1">
					<div class="position-relative">
						<div
							id="playBackBtn"
							class="playback-trigger pointer"
							@click="togglePlaybackPopup"
						>
							{{ currentPlaybackRate }}x
						</div>

						<div
							id="playBackContainer"
							v-if="showPlaybackPopup"
							class="playback-popup position-absolute bg-white border rounded shadow play-back-box"
							style="z-index: 1000; bottom: 30px; right: 0; width: 150px"
						>
							<div
								v-for="rate in playbackRates"
								:key="rate"
								@click="changePlaybackRate(rate)"
								:class="[
									'playback-rate-item',
									{ 'selected-rate': currentPlaybackRate === rate },
								]"
								style="cursor: pointer"
							>
								{{ rate }}x
							</div>
						</div>
					</div>
					<span @click="toggleMute" class="pointer">
						<img v-show="isMuted" src="@/assets/asset-bank/muted.svg" alt="" />
						<img
							v-show="!isMuted"
							src="@/assets/asset-bank/unmuted.svg"
							alt=""
						/>
					</span>
					<input
						type="range"
						min="0"
						max="1"
						step="0.01"
						:value="volume"
						@input="handleVolumeChange"
						style="width: 80px"
						class="seekbar"
						:style="{ '--progress': volume * 100 + '%' }"
					/>
					<span @click="toggleFullScreen" class="pointer">
						<img
							v-if="!isFullScreen"
							src="@/assets/asset-bank/fullscreen.svg"
							alt=""
						/>
						<img v-else src="@/assets/asset-bank/miniscreen.svg" alt="" />
					</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "VideoPlayer",
	props: {
		src: {
			type: String,
			required: true,
		},
		poster: {
			type: String,
			default: "",
		},
	},
	data() {
		return {
			isPlaying: false,
			currentTime: 0,
			videoDuration: 0,
			isFullScreen: false,
			currentPlaybackRate: 1,
			playbackRates: [0.5, 1, 1.25, 1.5, 2],
			showPlaybackPopup: false,
			volume: 1,
			isMuted: false,
			showControls: true,
			hideTimeout: null,
			isMouseOverControls: false,
		};
	},
	watch: {
		src() {
			const video = this.$refs.videoPlayer;
			this.isPlaying = false;
			this.currentTime = 0;

			if (video) {
				video.pause();
				video.load(); // reloads the new video
			}
		},
	},
	mounted() {
		const video = this.$refs.videoPlayer;
		document.addEventListener("fullscreenchange", this.handleFullscreenChange);
		video.addEventListener("timeupdate", () => {
			this.currentTime = video.currentTime;
		});
		video.addEventListener("loadedmetadata", () => {
			this.videoDuration = video.duration;
		});
		video.addEventListener("ended", () => {
			this.isPlaying = false;
			this.showControls = true;
		});
		document.addEventListener("click", this.handleClickOutside);

		document.addEventListener("keydown", this.handleSpacebar, true); // capture phase
	},
	beforeDestroy() {
		clearTimeout(this.hideTimeout);
		const video = this.$refs?.videoPlayer;
		document.removeEventListener(
			"fullscreenchange",
			this.handleFullscreenChange
		);
		if (video) {
			video.removeEventListener("timeupdate", this.updateTime);
			video.removeEventListener("loadedmetadata", this.setDuration);
		}
		document.removeEventListener("click", this.handleClickOutside);

		document.removeEventListener("keydown", this.handleSpacebar, true);
	},
	computed: {
		formattedCurrentTime() {
			return this.formatTime(this.currentTime);
		},
		formattedDuration() {
			return this.formatTime(this.videoDuration);
		},
	},
	methods: {
		handleInteraction() {
			this.showVideoControls();
			this.hideVideoControlsWithDelay();
		},

		showVideoControls() {
			this.showControls = true;
			clearTimeout(this.hideTimeout);
		},

		hideVideoControlsWithDelay() {
			clearTimeout(this.hideTimeout);

			this.hideTimeout = setTimeout(() => {
				const video = this.$refs.videoPlayer;

				// ✅ SAFETY CHECK: only proceed if video element exists
				if (video && !this.isMouseOverControls && !video.paused) {
					this.showControls = false;
				}
			}, 3000);
		},

		handleMouseLeave(event) {
			// If mouse leaves the wrapper and also not hovering controls, then hide
			const controlsEl = this.$refs.videoControls;
			if (!controlsEl.contains(event.relatedTarget)) {
				this.hideVideoControlsWithDelay();
			}
		},
		handleClickOutside(event) {
			const filterBtn = document.querySelector("#playBackBtn");
			const filterCard = document.querySelector("#playBackContainer");

			const clickedInside =
				(filterBtn && filterBtn.contains(event.target)) ||
				(filterCard && filterCard.contains(event.target));

			if (!clickedInside) {
				this.showPlaybackPopup = false;
				this.$forceUpdate();
			}
		},
		toggleMute() {
			const video = this.$refs.videoPlayer;
			if (this.isMuted) {
				video.volume = this.volume > 0 ? this.volume : 1;
				this.isMuted = false;
			} else {
				video.volume = 0;
				this.isMuted = true;
			}
		},
		handleVolumeChange(event) {
			const video = this.$refs.videoPlayer;
			const newVolume = parseFloat(event.target.value);
			video.volume = newVolume;
			this.volume = newVolume;
			this.isMuted = newVolume === 0;
		},
		formatTime(seconds) {
			const min = Math.floor(seconds / 60)
				.toString()
				.padStart(2, "0");
			const sec = Math.floor(seconds % 60)
				.toString()
				.padStart(2, "0");
			return `${min}:${sec}`;
		},
		togglePlayPause() {
			try {
				const video = this.$refs.videoPlayer;
				if (video.paused) {
					this.isPlaying = true;
					video.play();
					this.hideVideoControlsWithDelay(); // auto-hide allowed
				} else {
					this.isPlaying = false;
					video.pause();
					this.showControls = true; // Always show controls when paused
					clearTimeout(this.hideTimeout); // Stop any hide timer
				}
			} catch (error) {
				console.log(error);
			}
		},
		handleSpacebar(e) {
			const isSpace =
				e.code === "Space" || e.key === " " || e.key === "Spacebar";
			if (!isSpace) return;

			const tag = (e.target && e.target.tagName) || "";
			const isEditable =
				e.target &&
				(e.target.isContentEditable || ["INPUT", "TEXTAREA"].includes(tag));
			if (isEditable) return;

			e.preventDefault(); // stop scroll
			this.togglePlayPause(); // reuse your existing method
		},
		getVideoElement() {
			return this.$refs.videoPlayer;
		},
		onSeek() {
			const video = this.$refs.videoPlayer;
			video.currentTime = this.currentTime;
		},
		forward() {
			const video = this.$refs.videoPlayer;
			video.currentTime = Math.min(video.duration, video.currentTime + 10);
		},
		rewind() {
			const video = this.$refs.videoPlayer;
			video.currentTime = Math.max(0, video.currentTime - 10);
		},
		handleFullscreenChange() {
			this.isFullScreen = !!document.fullscreenElement;
		},
		toggleFullScreen() {
			const videoContainer = this.$el;

			if (!this.isFullScreen) {
				if (videoContainer.requestFullscreen) {
					videoContainer.requestFullscreen();
				} else if (videoContainer.webkitRequestFullscreen) {
					videoContainer.webkitRequestFullscreen();
				} else if (videoContainer.mozRequestFullScreen) {
					videoContainer.mozRequestFullScreen();
				} else if (videoContainer.msRequestFullscreen) {
					videoContainer.msRequestFullscreen();
				}
				this.isFullScreen = true;
			} else {
				if (document.exitFullscreen) {
					document.exitFullscreen();
				} else if (document.webkitExitFullscreen) {
					document.webkitExitFullscreen();
				} else if (document.mozCancelFullScreen) {
					document.mozCancelFullScreen();
				} else if (document.msExitFullscreen) {
					document.msExitFullscreen();
				}
				this.isFullScreen = false;
			}
		},
		togglePlaybackPopup() {
			this.showPlaybackPopup = !this.showPlaybackPopup;
		},
		changePlaybackRate(rate) {
			const video = this.$refs.videoPlayer;
			video.playbackRate = rate;
			this.currentPlaybackRate = rate;
			this.showPlaybackPopup = false;
		},
	},
};
</script>

<style>
.bg-black {
	background-color: black;
}
.h-250 {
	height: 250px;
}

.custom-controls {
	position: absolute;
	bottom: 5px;
	left: 0;
	right: 0;
	background: rgba(0, 0, 0, 0.6);
	padding: 10px;
	display: flex;
	flex-direction: column;
	color: white;
	z-index: 10;
	font-family: Lato;
	font-weight: 500 !important;
	font-size: 16px;
	color: white !important;
}
.custom-controls.video {
	position: absolute;
	bottom: 0;
	left: 0;
	width: 100%;
	background: rgba(0, 0, 0, 0.6);
	transform: translateY(100%);
	transition: transform 0.3s ease, opacity 0.3s ease;
	pointer-events: none; /* disables unwanted interactions when hidden */
	opacity: 0;
}

.custom-controls.video.show {
	transform: translateY(0%);
	opacity: 1;
	pointer-events: auto;
}
.play-back-box {
	background: #191919cc !important;
	box-shadow: 0px 2px 6px 2px #00000026 !important;
	box-shadow: 0px 1px 2px 0px #0000004d !important;
	backdrop-filter: blur(8px);
	font-family: Lato;
	font-weight: 600 !important;
	font-size: 16px;
	color: white !important;
	border: none !important;
}
.playback-rate-item {
	cursor: pointer;
	padding: 6px 30px;
	display: block;
	width: 100%;
	transition: background-color 0.2s;
}

.playback-rate-item:hover {
	background: var(--State-Layers-Surface-Variant-Opacity-08, #e7e0ec14);
}
.custom-control.video input {
	height: 2px !important;
}
.seekbar {
	cursor: pointer;
	width: 100%;
	height: 2px;
	appearance: none;
	-webkit-appearance: none;
	background: linear-gradient(
		to right,
		white 0%,
		white var(--progress),
		transparent var(--progress),
		transparent 100%
	);
	border-radius: 2px;
	outline: none;
	padding: 0;
	margin: 0;
}

/* Chrome/Edge (WebKit) track */
.seekbar::-webkit-slider-runnable-track {
	height: 2px;
	background: transparent;
	border-radius: 2px;
	border: 1px solid rgba(255, 255, 255, 0.3);
}

/* Chrome/Edge (WebKit) thumb */
.seekbar::-webkit-slider-thumb {
	-webkit-appearance: none;
	height: 10px;
	width: 10px;
	background: white;
	border-radius: 50%;
	border: none;
	margin-top: -4px; /* Align vertically with 2px track */
	cursor: pointer;
	position: relative;
	z-index: 2;
}

/* Firefox track */
.seekbar::-moz-range-track {
	height: 2px;
	background: transparent;
	border-radius: 2px;
}

/* Firefox thumb */
.seekbar::-moz-range-thumb {
	height: 10px;
	width: 10px;
	background: white;
	border-radius: 50%;
	border: none;
	cursor: pointer;
}
</style>
